import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams, useNavigate, Navigate } from "react-router-dom";
import { staffLogOut } from "../../../store/redux/universeStaffReducer.js";
import "./ExecutiveDirectorWorkspace.css";
import "./ExecutiveDirectorWorkspace.rwd.css";
import "./ExecutiveDirectorWorkspace.reference.css";
import "./ExecutiveDirectorWorkspace.pages.css";
import EDCommandRail from "./navigation/EDCommandRail";
import EDHeader from "./navigation/EDHeader";
import EDRegionalIntelligenceOverlay from "./map/EDRegionalIntelligenceOverlay";
import EDMobileNavigation from "./navigation/EDMobileNavigation";
import EDDashboard from "./dashboard/EDDashboard";
import EDSDDManagement from "./sdd-management/EDSDDManagement";
import EDFDCManagement from "./fdc-management/EDFDCManagement";
import EDClients from "./clients/EDClients";
import EDTargets from "./targets/EDTargets";
import EDPerformance from "./performance/EDPerformance";
import EDDiscipline from "./discipline/EDDiscipline";
import EDWallet from "./wallet/EDWallet";
import EDReports from "./reports/EDReports";
import EDTraining from "./training/EDTraining";
import EDAdministration from "./administration/EDAdministration";
import EDNotifications from "./notifications/EDNotifications";
import EDProfile from "./profile/EDProfile";
import EDSettings from "./settings/EDSettings";
import { getEDDashboard, logoutED } from "./services/edApi";
import EDFDCOnboarding from "./onboarding/EDFDCOnboarding";
import StaffClientOnboarding from "../shared-onboarding/StaffClientOnboarding.jsx";

export default function ExecutiveDirectorWorkspace() {
  const staffResource = useSelector(state => state?.universeStaffs);
  const activeStaff = staffResource?.activeStaff;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const view = params.get("view") || "dashboard";
  const [mobile, setMobile] = useState(false);
  const [regionalIntelligenceOpen, setRegionalIntelligenceOpen] = useState(false);
  const workspaceRef = useRef(null);
  const mapTriggerRef = useRef(null);
  const [compact, setCompact] = useState(false);
  const [theme, setTheme] = useState("light");
  const [data, setData] = useState({ staff: activeStaff || {} });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const staffId = activeStaff?.staffId;
  useEffect(() => { setData(prev => ({ ...prev, staff: activeStaff || {} })); }, [activeStaff]);
  const reload = useCallback(() => {
    if (!staffId) return Promise.resolve();
    setLoading(true); setError("");
    return getEDDashboard(staffId).then(result => { setData(prev => ({ ...prev, ...(result.data || {}), staff: activeStaff })); }).catch(failure => {
      if (failure?.response?.status !== 404) setError(failure?.response?.data?.message || "Unable to refresh regional data.");
    }).finally(() => setLoading(false));
  }, [staffId, activeStaff]);
  useEffect(() => { reload(); }, [reload]);
  // Regional Intelligence is an overlay, not a separate browser route.
  // Accept the old map key from bookmarks/shortcuts without exposing two menu items.
  const go = useCallback(key => {
    setMobile(false);
    if (key === "region" || key === "map") {
      mapTriggerRef.current = document.activeElement;
      setRegionalIntelligenceOpen(true);
      return;
    }
    setParams({ view: key });
  }, [setParams]);
  // Older ?view=map and ?view=region links open the same map above the dashboard.
  useEffect(() => {
    if (view !== "map" && view !== "region") return;
    mapTriggerRef.current = document.activeElement;
    setRegionalIntelligenceOpen(true);
    setParams({ view: "dashboard" }, { replace: true });
  }, [view, setParams]);
  // Keep the dashboard inaccessible to keyboard/screen readers while its modal is open.
  useEffect(() => {
    const node = workspaceRef.current;
    if (node) node.inert = regionalIntelligenceOpen;
    return () => { if (node) node.inert = false; };
  }, [regionalIntelligenceOpen]);
  const logout = async () => { try { if (staffId) await logoutED(staffId); } catch (_) { /* Clear the client session even if the server is unreachable. */ } finally { dispatch(staffLogOut()); navigate("/administration/executive/login/ed", { replace: true }); } };
  const props = { staffId, activeStaff, permissions: activeStaff?.role?.permissions || {}, dashboard: data, onNavigate: go, theme, onThemeChange: setTheme };
  const render = () => { switch (view) {
    case "sdds": return <EDSDDManagement {...props}/>;
    case "fdcs": return <EDFDCManagement {...props}/>;
    case "register-fdc": return <EDFDCOnboarding/>;
    case "onboard-client": return <StaffClientOnboarding role="ED" onNavigate={go}/>;
    case "clients": return <EDClients {...props}/>;
    case "targets": return <EDTargets {...props}/>;
    case "performance": case "states": return <EDPerformance {...props}/>;
    case "discipline": return <EDDiscipline {...props}/>;
    case "wallet": return <EDWallet {...props}/>;
    case "reports": return <EDReports {...props}/>;
    case "training": return <EDTraining {...props}/>;
    case "administration": return <EDAdministration {...props}/>;
    case "notifications": return <EDNotifications {...props}/>;
    case "profile": return <EDProfile/>;
    case "settings": return <EDSettings {...props}/>;
    default: return <EDDashboard data={{ ...data, staff: activeStaff }} staffId={staffId} loading={loading} onRefresh={reload} onNavigate={go} onOpenMap={() => go("region")}/>;
  }};
  if (!staffId || (activeStaff?.role?.name && activeStaff.role.name !== "ED")) return <Navigate to="/administration/executive/login/ed" replace/>;
  return <>
    <div ref={workspaceRef} className={`edWorkspace edReferenceTheme ${compact ? "edCompactLayout" : ""}`} data-ed-theme={theme}>
    <EDCommandRail view={view} onChange={go} onLogout={logout} staff={activeStaff} compact={compact} onToggleCompact={() => setCompact(value => !value)}/>
    <EDMobileNavigation open={mobile} onClose={() => setMobile(false)} view={view} onChange={go} onLogout={logout} staff={activeStaff}/>
    <main className="edMain"><EDHeader staff={activeStaff} view={view} onMenu={() => setMobile(true)} onNavigate={go} theme={theme} onThemeChange={() => setTheme(value => value === "light" ? "dark" : "light")} onLogout={logout}/>
      {error && <div className="edGlobalError" role="alert">{error} <button onClick={reload}>Retry</button></div>}
      <div className="edContent">{render()}</div>
    </main>
    </div>
    {regionalIntelligenceOpen && <EDRegionalIntelligenceOverlay onClose={() => setRegionalIntelligenceOpen(false)} returnFocusTo={mapTriggerRef.current} />}
  </>;
}
